import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { KPICard } from '../components/KPICard';
import { TrafficLight } from '../components/TrafficLight';
import { ForecastLoader } from '../components/ForecastLoader';
import { getWeeklySummary } from '../lib/api';
import { formatNumber, formatCurrency } from '../lib/utils';
import { 
  TrendingUp, 
  Users, 
  Briefcase, 
  AlertTriangle,
  ArrowRight,
  Bell,
  Calendar,
  CheckCircle2
} from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import { useNavigate } from 'react-router-dom';

const ResumenSemanal = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getWeeklySummary();
        setData(response.data);
      } catch (error) {
        console.error('Error fetching weekly summary:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="space-y-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Resumen Semanal</h1>
            <p className="text-slate-500 mt-1">Cargando datos del sistema...</p>
          </div>
          <ForecastLoader type="forecast" />
        </div>
      </Layout>
    );
  }

  const alertTypeIcons = {
    risk: AlertTriangle,
    opportunity: TrendingUp,
    closing: Calendar,
  };

  const alertTypeColors = {
    risk: 'text-rose-600 bg-rose-50 border-rose-200',
    opportunity: 'text-amber-600 bg-amber-50 border-amber-200',
    closing: 'text-indigo-600 bg-indigo-50 border-indigo-200',
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="resumen-semanal-page">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Resumen Semanal</h1>
            <p className="text-slate-500 mt-1">
              Actualizado: {new Date(data?.generated_at).toLocaleDateString('es-ES', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <KPICard
            title="Volumen Proyectado"
            value={formatNumber(data?.projected_volume)}
            subtitle="kgs próximo mes"
            icon={TrendingUp}
            variant="primary"
          />
          <KPICard
            title="Clientes Activos"
            value={data?.active_clients}
            subtitle="en cartera"
            icon={Users}
          />
          <KPICard
            title="Oportunidades Abiertas"
            value={data?.open_opportunities}
            subtitle="en pipeline"
            icon={Briefcase}
          />
          <KPICard
            title="Clientes en Riesgo"
            value={data?.at_risk_clients}
            subtitle="requieren atención"
            icon={AlertTriangle}
            variant={data?.at_risk_clients > 3 ? 'danger' : 'default'}
          />
        </div>

        {/* Summary Text */}
        <div className="bg-gradient-to-r from-indigo-500 to-violet-600 rounded-2xl p-6 text-white shadow-lg shadow-indigo-500/20">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-semibold mb-2">Resumen Ejecutivo</h2>
              <p className="text-white/90 leading-relaxed">{data?.summary_text}</p>
            </div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Alerts */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Bell className="w-5 h-5 text-slate-400" />
                Alertas Activas
              </h2>
              <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
                {data?.alerts?.length || 0}
              </span>
            </div>
            <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
              {data?.alerts?.slice(0, 6).map((alert, i) => {
                const Icon = alertTypeIcons[alert.type] || Bell;
                return (
                  <div key={i} className="p-4 hover:bg-slate-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${alertTypeColors[alert.type]}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900">{alert.client_name}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{alert.message}</p>
                      </div>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        alert.severity === 'high' ? 'bg-rose-100 text-rose-700' :
                        alert.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {alert.severity === 'high' ? 'Alta' : alert.severity === 'medium' ? 'Media' : 'Baja'}
                      </span>
                    </div>
                  </div>
                );
              })}
              {(!data?.alerts || data.alerts.length === 0) && (
                <div className="p-8 text-center text-slate-500">
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                  <p>Sin alertas activas</p>
                </div>
              )}
            </div>
          </div>

          {/* Priority Ranking */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="p-5 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900">Prioridades de la Semana</h2>
              <p className="text-sm text-slate-500 mt-0.5">Clientes con mayor potencial de acción</p>
            </div>
            <div className="divide-y divide-slate-100">
              {data?.priority_ranking?.map((item, i) => (
                <div 
                  key={i} 
                  className="p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                  onClick={() => navigate('/clientes')}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center text-sm font-bold text-slate-600">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <TrafficLight status={item.status} size="sm" />
                        <p className="text-sm font-medium text-slate-900 truncate">{item.client_name}</p>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{item.reason}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-slate-900">
                        {formatNumber(item.untapped_potential)} kgs
                      </p>
                      <p className="text-xs text-slate-500">potencial</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ResumenSemanal;
